//libs
import React from 'react';
import ReactDOM from 'react-dom';
import { nanoid } from 'nanoid';
import { Plugin, PluginKey } from 'prosemirror-state';
//types
import { EditorView } from 'prosemirror-view';

import { BaseExtension, EditorOptions, extensionType } from '@lyearn/lygo-core';

//utils
import {
  isInCallout,
  isInTable,
  isInTabs,
  isInToggleList,
  removeMenuContainer,
  setPluginMeta,
} from '../../utils';
//components
import { TooltipMenu } from './TooltipMenu';
import { TooltipMenuBlockType } from './types';
import { isDividerSelection, isFlashCardSelection, isImageSelection } from './utils';

export const tooltipMenuKey = new PluginKey('tooltip-menu-plugin');

const TOOLTIP_MENU_CONTAINER_ID = 'tooltip-menu-container';

const selectionHandling = (editorView: EditorView) => {
  const isDividerSelected = isDividerSelection(editorView.state);
  const isCalloutSelected = isInCallout(editorView.state);
  const isImageSelected = isImageSelection(editorView.state);
  const isFlashCardSelected = isFlashCardSelection(editorView.state);

  let props = {
    open: false,
    editorView: editorView,
    type: isDividerSelected
      ? TooltipMenuBlockType.Divder
      : isCalloutSelected
      ? TooltipMenuBlockType.Callout
      : isFlashCardSelected
      ? TooltipMenuBlockType.FlashCard
      : TooltipMenuBlockType.Media,
  };

  const disableMenu =
    isInTable(editorView, editorView.state.selection.$from.pos) ||
    isInTabs(editorView, editorView.state.selection.$from.pos) ||
    isInToggleList(editorView, editorView.state.selection.$from.pos);

  if (
    (isImageSelected || isDividerSelected || isCalloutSelected || isFlashCardSelected) &&
    (editorView.state.selection.$from.pos !== editorView.state.selection.$to.pos ||
      isCalloutSelected) &&
    !disableMenu
  ) {
    props.open = true;
  }
  setPluginMeta(editorView, tooltipMenuKey, props);
  return false;
};

export default class TooltipToolbar extends BaseExtension {
  get type(): string {
    return extensionType.toolbar;
  }

  get name(): string {
    return 'tooltip_menu';
  }

  plugins({ portalApi }: EditorOptions) {
    return [
      new Plugin({
        key: tooltipMenuKey,
        state: {
          init() {
            return { open: false };
          },
          apply(tr, old) {
            const props = tr.getMeta(tooltipMenuKey);
            if (props) return props;
            return old;
          },
        },
        view: (editorView) => {
          // This will be called only once. It will add a container div.
          // Tooltip Menu Button will be rendered in this container div.
          let tooltipMenuContainer = document.createElement('div');
          tooltipMenuContainer.id = TOOLTIP_MENU_CONTAINER_ID;
          editorView?.dom?.parentNode?.appendChild(tooltipMenuContainer);
          const portalKey = nanoid();

          // Need to return the following functions in the view function
          return {
            update: (view) => {
              const state = view.state;
              let props = tooltipMenuKey.getState(state);

              if (
                props.editorView &&
                props.editorView.state.selection.$from.pos ===
                  props.editorView.state.selection.$to.pos &&
                !isInCallout(view.state)
              ) {
                props.open = false;
              }

              const dom = view?.dom?.parentNode?.querySelector(`#${TOOLTIP_MENU_CONTAINER_ID}`);
              if (dom) {
                portalApi.createPortal(
                  ReactDOM.createPortal(<TooltipMenu {...props} />, dom, portalKey),
                );
              }
            },
            destroy: () => {
              portalApi.destroyPortal(portalKey);
              removeMenuContainer(TOOLTIP_MENU_CONTAINER_ID, editorView);
            },
          };
        },
        props: {
          handleDOMEvents: {
            click: selectionHandling,
          },
        },
      }),
    ];
  }
}
